'use client';

import { c as _c } from "react/compiler-runtime";
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import type { GraphQLVolunteerEvent } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { type VolunteerSlotStatusUpdateRequest, type VolunteerSlotStatusUpdateResponse } from '@sola/schema/dotcom/supporters/volunteers.ts';
import { normalizeVolunteerStatus } from '@sola/shared-utils/supporters/volunteers.ts';
import { MUTATION_KEYS, QUERY_KEYS } from '@sola/supporter-sdk/constants.ts';
import { useReadyForQueries, useSupporterMutation } from '@sola/supporter-sdk/hooks/base.ts';
import { updateVolunteerSlots } from './requests.ts';
const getVolunteerSlotKey = (contactId: string, slotId: string) => JSON.stringify([contactId, slotId]);
export const updateCachedVolunteerEvents = (queryClient: QueryClient, globalId: string | undefined, successfulUpdates: VolunteerSlotStatusUpdateResponse['successfulUpdates']) => {
  const successfulUpdatesBySlot = new Map(successfulUpdates.map(({
    contactId,
    slotId,
    status
  }) => [getVolunteerSlotKey(contactId, slotId), normalizeVolunteerStatus(status)]));
  queryClient.setQueryData<GraphQLVolunteerEvent[]>([QUERY_KEYS.supporterVolunteerEvents, globalId], volunteerEvents => volunteerEvents?.map(event => ({
    ...event,
    slots: event.slots.map(slot => {
      const status = successfulUpdatesBySlot.get(getVolunteerSlotKey(slot.contactId, slot.id));
      return status ? {
        ...slot,
        status
      } : slot;
    })
  })));
};

/**
 * Update volunteer slot statuses and patch the supporter's cached events.
 */
export const useUpdateVolunteerSlot = () => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  const {
    globalId
  } = useReadyForQueries();
  let t0;
  if ($[0] !== globalId || $[1] !== queryClient) {
    t0 = t1 => {
      const {
        successfulUpdates
      } = t1;
      if (successfulUpdates.length === 0) {
        throw new Error("No volunteer slots were updated.");
      }
      updateCachedVolunteerEvents(queryClient, globalId, successfulUpdates);
    };
    $[0] = globalId;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const handleSuccess = t0;
  let t1;
  let t2;
  if ($[3] !== globalId) {
    t1 = [MUTATION_KEYS.volunteerSlotUpdate, globalId];
    t2 = updates => updateVolunteerSlots(globalId, updates);
    $[3] = globalId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  let t3;
  if ($[6] !== handleSuccess || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      mutationKey: t1,
      mutationFn: t2,
      mockMutationFn: _temp2,
      mockOnSuccess: handleSuccess,
      onSuccess: handleSuccess
    };
    $[6] = handleSuccess;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useSupporterMutation(t3);
};
function _temp(resolve) {
  return setTimeout(resolve, 1000);
}
async function _temp2(updates_0) {
  await new Promise(_temp);
  return {
    failedUpdates: [],
    successfulUpdates: updates_0
  };
}